import { axiosInstance } from "api/axios.instance"
import type {
	TCreateFigureHighlightRequest,
	TCreateFreehandHighlightRequest,
	TFigureHighlight,
	TFreehandHighlight,
	THighlightShapeType,
} from "types/elevationReferences.type"

// REQ-400: highlight create + delete — figures (rectangle/ellipse, #1323) and freehand (#1324).
// Kept separate from `AnnotationService` (rendering-only, per #1378) and from `quotations.service` —
// the highlights domain has its own small surface (three endpoints) so it gets its own file per the
// service → hook → component convention in AGENTS.md.
class HighlightsService {
	static QUOTATIONS = "quotations"
	static HIGHLIGHTS = "highlights"

	async createFigureHighlight(quotationId: number, body: TCreateFigureHighlightRequest) {
		return axiosInstance.post<TFigureHighlight>(
			`${HighlightsService.QUOTATIONS}/${quotationId}/${HighlightsService.HIGHLIGHTS}/figures/`,
			body,
		)
	}

	// #1324. Separate endpoint from the figure one, with a genuinely different body: `path` +
	// `stroke_width` and no `fill_color` / `line_style` (see TCreateFreehandHighlightRequest). The
	// response is the created highlight itself, carrying a synthesized `shape_type: "freehand"`.
	async createFreehandHighlight(quotationId: number, body: TCreateFreehandHighlightRequest) {
		return axiosInstance.post<TFreehandHighlight>(
			`${HighlightsService.QUOTATIONS}/${quotationId}/${HighlightsService.HIGHLIGHTS}/freehand/`,
			body,
		)
	}

	// `highlight_type` is a REQUIRED query param, and it is not cosmetic: figures and freehand live
	// in separate backend tables, so `highlight_id` alone does not identify a row (a wrong or missing
	// value is a 400, or worse deletes nothing). Callers pass the highlight's own `shape_type`;
	// mapping rectangle/ellipse → "figure" happens here so no call site has to remember it. No
	// `version` — the backend model has none, so this is not under optimistic locking.
	async deleteHighlight(quotationId: number, highlightId: number, shapeType: THighlightShapeType) {
		return axiosInstance.delete(
			`${HighlightsService.QUOTATIONS}/${quotationId}/${HighlightsService.HIGHLIGHTS}/${highlightId}/`,
			{ params: { highlight_type: shapeType === "freehand" ? "freehand" : "figure" } },
		)
	}
}

export default new HighlightsService()
